import { FormType } from '../../interfaces/form.interface';
import type { ReactiveFormHostMode } from './reactive-form-host-state';

export interface ReactiveFormHostModeResolution {
    readonly formType: FormType;
    readonly mode: ReactiveFormHostMode;
    readonly flag: ReactiveFormHostMode;
}

export interface ReactiveFormHostModelModeOptions<TModel> {
    readonly model: TModel | null | undefined;
    readonly idField?: string | undefined;
}

const duplicatePathToken = 'duplicar-';
const editPathToken = 'editar-';
const createPathToken = 'crear-';
const defaultIdentifierField = 'id';

/** Resolves host form modes from route paths, form types and persisted models. */
export class ReactiveFormHostModeResolver {
    public static resolveFromPath(path: string | null | undefined): ReactiveFormHostModeResolution {
        const formType = this.resolveFormTypeFromPath(path);

        return {
            formType,
            mode: this.resolveModeFromFormType(formType),
            flag: this.resolveModeFromFormType(formType),
        };
    }

    public static resolveFormTypeFromPath(path: string | null | undefined): FormType {
        if (path?.includes(duplicatePathToken)) {
            return FormType.DUPLICATION;
        }

        if (path?.includes(editPathToken)) {
            return FormType.EDITION;
        }

        if (path?.includes(createPathToken)) {
            return FormType.CREATION;
        }

        return FormType.CREATION;
    }

    public static resolveModeFromFormType(formType: FormType | null | undefined): ReactiveFormHostMode {
        switch (formType) {
        case FormType.CREATION:
            return 'new';
        case FormType.EDITION:
            return 'edit';
        case FormType.DUPLICATION:
            return 'duplicate';
        case FormType.INFORMATIVE:
            return 'read';
        default:
            return '';
        }
    }

    public static resolveModeFromModel<TModel>(options: ReactiveFormHostModelModeOptions<TModel>): ReactiveFormHostMode {
        const model = options.model as Record<string, unknown> | null | undefined;
        const idField = options.idField ?? defaultIdentifierField;

        return model?.[idField] ? 'edit' : 'new';
    }

    public static normalizeMode(mode: string | null | undefined): ReactiveFormHostMode {
        switch (mode) {
        case 'new':
        case 'edit':
        case 'duplicate':
        case 'read':
            return mode;
        default:
            return '';
        }
    }
}
